import {
  fetchCostDrivers,
  fetchNewsAffectingPlans,
  fetchSizeOfMarket,
  fetchKeyChallengesAndTrends,
  fetchPorterFiveForces,
  fetchExistingSuppliers,
  fetchGeneralNews,
  fetchCategorySpecificNews,
  fetchFinancialReports,
  fetchAccreditations,
  fetchRegulations,
  fetchSuppliersToShow,
  postCategoryHistory,
  fetchCategoryHistory,
  fetchSpendOverview,
  fetchSpendByBusinessUnit,
  fetchSpendByDepartment,
  fetchSpendBySupplier,
  fetchSpendL3Insights,
  fetchSpendProcurementLevers,
  fetchSpendCostDrivers,
  fetchSpendInflationOverview,
  fetchSpendCommodityInflation,
} from "../lib/api_routes";
import { createTaxonomyPath } from "../utils/functions";
import { formatAxiosError } from "../utils/error_message";

export const generateMarketInsights = async (options) => {
  const {
    trackAction,
    resetSession,
    onComplete,
    values,
    isValid,
    setTaxonomyPath,
    historyId,
    setCurrentEntryId,
    accountInfo,
    setIsLoading,
    setIsLoaded,
    setLoadedCount,
    closeForm,
    setTotalToLoad,
    incrementLoadedCount,
    setIsGenerating,
    setHistory,
    setCostDrivers,
    setNewsAffectingPlans,
    setSizeOfMarket,
    setKeyChallengesAndTrends,
    setPorterFiveForces,
    setGeneralNews,
    setCategorySpecificNews,
    setFinancialReports,
    setSuppliers,
    setAccreditations,
    setRegulations,
    setSuppliersToShow,
    setExistingSuppliers,
    formatSuppliersData,
    formatExistingSuppliersData,
    setSpendOverview,
    setSpendBySupplier,
    setSpendByBusinessUnit,
    setSpendByDepartment,
    setSpendL3Insights,
    setSpendProcurementLevers,
    setSpendCostDrivers,
    setSpendInflationOverview,
    setSpendCommodityInflation,
  } = options;

  // Validate the form before proceeding
  if (!isValid()) return;

  // Taxonomy path
  setTaxonomyPath(createTaxonomyPath(values, accountInfo));

  setIsLoading(true);
  setIsLoaded(false);
  setLoadedCount(0);
  closeForm();

  const apiCalls = [
    fetchCostDrivers,
    fetchNewsAffectingPlans,
    fetchSizeOfMarket,
    fetchKeyChallengesAndTrends,
    fetchPorterFiveForces,
    fetchExistingSuppliers,
    () =>
      fetchGeneralNews({
        country: values.country,
        accountId: accountInfo?.id,
        offset: 0,
        limit: 20,
      }),
    () =>
      fetchCategorySpecificNews({
        country: values.country,
        accountId: accountInfo?.id,
        category: values.category,
      }),
    fetchFinancialReports,
    fetchAccreditations,
    fetchRegulations,
    fetchSuppliersToShow,
  ];

  const spendApiCalls = accountInfo?.showSpend
    ? [
        fetchSpendOverview,
        fetchSpendBySupplier,
        fetchSpendByBusinessUnit,
        fetchSpendByDepartment,
        fetchSpendL3Insights,
        fetchSpendProcurementLevers,
        fetchSpendCostDrivers,
        fetchSpendInflationOverview,
        fetchSpendCommodityInflation,
      ]
    : [];

  const allCalls = [...apiCalls, ...spendApiCalls];

  setTotalToLoad(allCalls.length);

  try {
    const results = await Promise.all(
      allCalls.map((call) =>
        call(values).then((res) => {
          incrementLoadedCount();
          return res;
        }),
      ),
    );

    const [
      costDrivers,
      newsData,
      sizeData,
      keyChallengesAndTrendsData,
      porterData,
      existingSuppliersData,
      generalNews,
      categorySpecificNews,
      financialReports,
      accreditationsData,
      regulationsData,
      suppliersToShowData,
    ] = results;

    const [
      spendOverviewData,
      spendBySupplierData,
      spendByBusinessUnitData,
      spendByDepartmentData,
      spendL3InsightsData,
      spendProcurementLeversData,
      spendCostDriversData,
      spendInflationOverviewData,
      spendCommodityInflationData,
    ] = accountInfo?.showSpend ? results.slice(apiCalls.length) : [];

    // Set all the state directly in the helper
    setCostDrivers(costDrivers);
    setNewsAffectingPlans(newsData);
    setSizeOfMarket(sizeData);
    setKeyChallengesAndTrends(keyChallengesAndTrendsData);
    setPorterFiveForces(porterData);
    setGeneralNews(generalNews);
    setCategorySpecificNews(categorySpecificNews);
    setFinancialReports(financialReports);
    setAccreditations(accreditationsData);
    setRegulations(regulationsData);
    setSuppliersToShow(formatSuppliersData(suppliersToShowData || []));
    setExistingSuppliers(
      formatExistingSuppliersData(existingSuppliersData || []),
    );

    // Only render when user has access to Spend section
    if (accountInfo?.showSpend) {
      setSpendOverview(spendOverviewData?.summary ?? null);
      setSpendBySupplier(spendBySupplierData?.data ?? null);
      setSpendByBusinessUnit(spendByBusinessUnitData?.data ?? null);
      setSpendByDepartment(spendByDepartmentData?.data ?? null);
      setSpendL3Insights(spendL3InsightsData?.insights ?? null);
      setSpendProcurementLevers(spendProcurementLeversData?.levers ?? null);
      setSpendCostDrivers(spendCostDriversData?.data ?? null);
      setSpendInflationOverview(spendInflationOverviewData?.data ?? null);
      setSpendCommodityInflation(spendCommodityInflationData?.data ?? null);
      setIsGenerating(true);
    }

    if (!historyId) {
      const newEntry = await postCategoryHistory(values);
      // Set the current entry ID to the newly created entry
      if (newEntry && newEntry.id) {
        setCurrentEntryId(newEntry.id);
      }
    } else {
      setCurrentEntryId(historyId);
    }

    // USER ACTIVITY TRACKING - success
    await trackAction("Generate Insight", "SUCCESS", null, values);

    // Refetch updated history
    const updatedData = await fetchCategoryHistory();
    if (updatedData) {
      setHistory(updatedData);

      // If there's no new entry ID yet, find it in the updated history
      if (!historyId && updatedData.length > 0) {
        // Sorting the history by date
        const sortedHistory = [...updatedData].sort((a, b) => {
          const timeA = new Date(a.time).getTime();
          const timeB = new Date(b.time).getTime();
          return timeB - timeA;
        });
        setCurrentEntryId(sortedHistory[0].id);
      }
    }
    return {
      costDrivers,
      newsData,
      sizeData,
      keyChallengesAndTrendsData,
      porterData,
      existingSuppliersData,
      generalNews,
      categorySpecificNews,
      financialReports,
      accreditationsData,
      regulationsData,
      suppliersToShowData,
      spendOverviewData,
      spendBySupplierData,
      spendByBusinessUnitData,
      spendByDepartmentData,
      spendL3InsightsData,
      spendProcurementLeversData,
      spendCostDriversData,
      spendInflationOverviewData,
      spendCommodityInflationData,
    };
  } catch (error) {
    const { serverMessage, fullError } = await formatAxiosError(error);
    alert(serverMessage ?? "Failed to generate insights. Please try again.");
    // USER ACTIVITY TRACKING
    if (trackAction) {
      await trackAction("Generate Insight", "FAILED", fullError, values);
      resetSession();
    }
  } finally {
    onComplete();
  }
};
